import {
  ParentCompanyData,
  ParentCompanyRequest,
  useCreateParentCompanyMutation,
  useParentCompany,
  useUpdateParentCompanyMutation,
} from '@api'
import {FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {errorAtom, isFormDirtyAtom, handleNavigateAtom, pageLinksAtom} from '@state'

import {useEffect, useState} from 'react'

import {FormFieldItem, generateReadableErrorMessages, useViewNavigate, validateFields} from 'common'
import {useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

/**
 * Initial data
 */
const initialParentCompanyData: ParentCompanyData = {
  name: '',
  description: '',
  note: '',
  active: true,
}

export function useParentCompanyFormModel() {
  const setError = useSetAtom(errorAtom)
  const setIsFormDirty = useSetAtom(isFormDirtyAtom)
  const handleNavigate = useSetAtom(handleNavigateAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [parentCompanyData, setParentCompanyData] = useState<ParentCompanyData>(initialParentCompanyData)
  const [companyFormFieldsErrors, setCompanyFormFieldsErrors] = useState<string[]>([])
  const navigate = useViewNavigate()
  const {id} = useParams()
  const {t} = useTranslation()

  const {data: parentCompanyResponse} = useParentCompany(id)
  const {mutate: updateParentCompanyMutation} = useUpdateParentCompanyMutation()
  const {mutate: createParentCompanyMutation} = useCreateParentCompanyMutation()

  useEffect(() => {
    if (parentCompanyResponse && id)
      setParentCompanyData({
        active: parentCompanyResponse.active,
        description: parentCompanyResponse.description,
        name: parentCompanyResponse.name,
        note: parentCompanyResponse.note,
        id: parentCompanyResponse.id,
      })
  }, [parentCompanyResponse, id])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Resources), link: NavigationPathKey.RESOURCES_DASHBOARD},
        {
          title: t(TranslationKey['Amplio partners']),
          link: NavigationPathKey.PARENT_COMPANY_SEARCH,
        },
        {title: id ? t(TranslationKey['Edit partner']) : t(TranslationKey['New partner'])},
      ]),
    [id],
  )

  /**
   * A function handling Parent Company form fields change
   * @param event Change event
   * @param checked Checked state for checkbox form fields
   */
  const onChangeCompanyGeneralFormField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    checked?: boolean,
  ) => {
    setIsFormDirty(true)
    const fieldName = event.target.name
    const value = checked ?? event.target.value
    setCompanyFormFieldsErrors(prev => prev.filter(item => item !== fieldName))
    setParentCompanyData(prev => ({...prev, [fieldName]: value}))
  }

  /**
   * Parent Company form fields configuration - general section
   */
  const parentCompanyGeneralFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey['Partner name']),
      name: 'name',
      onChange: onChangeCompanyGeneralFormField,
      value: parentCompanyData.name,
      error: companyFormFieldsErrors.includes('name'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Description),
      name: 'description',
      onChange: onChangeCompanyGeneralFormField,
      value: parentCompanyData.description,
      error: companyFormFieldsErrors.includes('description'),
    },
    {
      fieldType: FormFieldType.INPUT,
      label: t(TranslationKey.Note),
      name: 'note',
      onChange: onChangeCompanyGeneralFormField,
      value: parentCompanyData.note,
      error: companyFormFieldsErrors.includes('note'),
    },
    {
      fieldType: FormFieldType.CHECKBOX_GROUP,
      label: t(TranslationKey.Active),
      name: 'active',
      itemsInColumn: 1,
      checkboxes: [
        {
          checked: parentCompanyData.active,
          label: t(TranslationKey.Active),
          value: parentCompanyData.active,
          name: 'active',
          onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
            onChangeCompanyGeneralFormField(event, checked),
        },
      ],
    },
  ]

  /**
   * A custom validation function for Parent Company form data
   * @returns If there are any invalid fields
   */
  const validateData = (): boolean => {
    const notRequiredFields: string[] = ['image', 'note', 'description']

    const invalidValues = validateFields(parentCompanyData).filter(item => !notRequiredFields.includes(item))

    const isFormInvalid = invalidValues.length > 0
    if (isFormInvalid) {
      setError({
        message: generateReadableErrorMessages(t, invalidValues),
        name: '',
      })

      setCompanyFormFieldsErrors(invalidValues)
    }
    return isFormInvalid
  }

  /**
   * A function handling the Save button click
   * If form is valid updates or saves a new Company
   * @returns void
   */
  const onSubmit = () => {
    if (validateData()) return

    const clearAndExit = (id: string) => {
      setIsFormDirty(false)
      navigate(`${NavigationPathKey.PARENT_COMPANY_VIEW}/${id}`)
    }

    if (id) {
      updateParentCompanyMutation(
        {
          id,
          body: parentCompanyData as ParentCompanyRequest,
        },
        {onSuccess: () => clearAndExit(id)},
      )
    } else {
      createParentCompanyMutation(parentCompanyData as ParentCompanyRequest, {onSuccess: ({id}) => clearAndExit(id)})
    }
  }

  /**
   * A function handling the Discard button click
   */
  const onDiscard = (): void => {
    handleNavigate(() => navigate(-1))
  }

  return {
    onSubmit,
    onDiscard,
    t,
    parentCompanyGeneralFormFields,
  }
}
